import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import PropertyGallery from "@/components/PropertyGallery";
import { Bed, Bath, Maximize, MapPin, Calendar, Phone, Mail, ArrowLeft, Check } from "lucide-react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PropertyDetail = () => {
  const { mlsNumber } = useParams();
  const [property, setProperty] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchProperty();
  }, [mlsNumber]);

  const fetchProperty = async () => {
    try {
      const response = await axios.get(`${API}/properties/${mlsNumber}`);
      setProperty(response.data);
    } catch (error) {
      console.error('Error fetching property:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('en-CA', {
      style: 'currency',
      currency: 'CAD',
      minimumFractionDigits: 0,
    }).format(price);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" data-testid="loading-indicator">
        <p className="text-muted-foreground text-lg">Loading property details...</p>
      </div>
    );
  }

  if (!property) {
    return (
      <div className="min-h-screen flex items-center justify-center" data-testid="not-found">
        <div className="text-center">
          <p className="text-2xl font-heading font-semibold text-primary mb-4">Property Not Found</p>
          <Link to="/properties">
            <Button className="bg-primary text-primary-foreground hover:bg-primary/90 h-12 px-8 rounded-none uppercase tracking-widest text-sm font-semibold transition-all duration-300">
              Back to Properties
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="property-detail-page">
      <section className="py-8 bg-secondary/30">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <Link to="/properties" className="inline-flex items-center text-sm text-muted-foreground hover:text-accent transition-colors" data-testid="back-link">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Properties
          </Link>
        </div>
      </section>

      <section className="py-12">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
            <div className="lg:col-span-2 space-y-8">
              <PropertyGallery images={property.images} />

              <div>
                <div className="mb-6">
                  <div className="flex items-center justify-between mb-4">
                    <span className="inline-block bg-accent text-accent-foreground px-4 py-2 text-sm font-semibold uppercase tracking-widest">
                      {property.property_type}
                    </span>
                    <span className="text-sm text-muted-foreground" data-testid="property-status">MLS #{property.mls_number}</span>
                  </div>
                  <h1 className="text-4xl md:text-5xl font-heading font-semibold text-primary mb-4" data-testid="property-title">
                    {property.address.street_number} {property.address.street_name} {property.address.street_type}
                  </h1>
                  <p className="flex items-center text-lg text-muted-foreground mb-6">
                    <MapPin className="w-5 h-5 mr-2" />
                    {property.address.city}, {property.address.province} {property.address.postal_code}
                  </p>
                  <p className="text-5xl font-heading font-bold text-accent" data-testid="property-price">{formatPrice(property.price)}</p>
                </div>

                <div className="grid grid-cols-2 md:grid-cols-4 gap-6 py-8 border-y border-border">
                  <div>
                    <div className="flex items-center justify-center mb-2">
                      <Bed className="w-6 h-6 text-accent" />
                    </div>
                    <p className="text-2xl font-semibold text-primary text-center">{property.bedrooms}</p>
                    <p className="text-sm uppercase tracking-widest text-muted-foreground text-center">Bedrooms</p>
                  </div>
                  <div>
                    <div className="flex items-center justify-center mb-2">
                      <Bath className="w-6 h-6 text-accent" />
                    </div>
                    <p className="text-2xl font-semibold text-primary text-center">{property.bathrooms}</p>
                    <p className="text-sm uppercase tracking-widest text-muted-foreground text-center">Bathrooms</p>
                  </div>
                  <div>
                    <div className="flex items-center justify-center mb-2">
                      <Maximize className="w-6 h-6 text-accent" />
                    </div>
                    <p className="text-2xl font-semibold text-primary text-center">{property.square_feet || 'N/A'}</p>
                    <p className="text-sm uppercase tracking-widest text-muted-foreground text-center">Sq Ft</p>
                  </div>
                  <div>
                    <div className="flex items-center justify-center mb-2">
                      <Calendar className="w-6 h-6 text-accent" />
                    </div>
                    <p className="text-2xl font-semibold text-primary text-center">{property.days_on_market}</p>
                    <p className="text-sm uppercase tracking-widest text-muted-foreground text-center">Days on Market</p>
                  </div>
                </div>
              </div>

              <div>
                <h2 className="text-2xl font-heading font-semibold text-primary mb-4">Property Description</h2>
                <p className="text-muted-foreground leading-relaxed" data-testid="property-description">{property.description}</p>
              </div>

              {property.features && property.features.length > 0 && (
                <div>
                  <h2 className="text-2xl font-heading font-semibold text-primary mb-4">Property Features</h2>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                    {property.features.map((feature, index) => (
                      <div key={index} className="flex items-center" data-testid={`feature-${index}`}>
                        <Check className="w-5 h-5 text-accent mr-2 flex-shrink-0" />
                        <span className="text-muted-foreground">{feature}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div>
                <h2 className="text-2xl font-heading font-semibold text-primary mb-4">Property Details</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="flex justify-between py-3 border-b border-border">
                    <span className="text-muted-foreground">Property Type</span>
                    <span className="font-semibold text-primary">{property.property_type}</span>
                  </div>
                  <div className="flex justify-between py-3 border-b border-border">
                    <span className="text-muted-foreground">Status</span>
                    <span className="font-semibold text-primary">{property.status}</span>
                  </div>
                  <div className="flex justify-between py-3 border-b border-border">
                    <span className="text-muted-foreground">Lot Size</span>
                    <span className="font-semibold text-primary">{property.lot_size || 'N/A'}</span>
                  </div>
                  <div className="flex justify-between py-3 border-b border-border">
                    <span className="text-muted-foreground">Listed Date</span>
                    <span className="font-semibold text-primary">{property.list_date}</span>
                  </div>
                </div>
              </div>
            </div>

            <div className="lg:col-span-1">
              <div className="sticky top-24">
                <Card className="border-2 border-accent" data-testid="agent-contact-card">
                  <CardContent className="p-8">
                    <div className="text-center mb-6">
                      <img
                        src="https://images.unsplash.com/photo-1592878995758-02b32ddabdd3?crop=entropy&cs=srgb&fm=jpg&q=85"
                        alt="Paul Murray"
                        className="w-32 h-32 object-cover mx-auto mb-4"
                      />
                      <h3 className="text-2xl font-heading font-semibold text-primary mb-2">{property.agent_name}</h3>
                      <p className="text-sm uppercase tracking-widest text-muted-foreground">Your GTA Real Estate Expert</p>
                    </div>

                    <div className="space-y-4 mb-8">
                      <a 
                        href={`tel:${property.agent_phone}`} 
                        className="flex items-center justify-center p-4 bg-secondary hover:bg-secondary/80 transition-colors"
                        data-testid="agent-phone-link"
                      >
                        <Phone className="w-5 h-5 mr-3 text-accent" />
                        <span className="font-semibold text-primary">{property.agent_phone}</span>
                      </a>
                      <a 
                        href={`mailto:${property.agent_email}`} 
                        className="flex items-center justify-center p-4 bg-secondary hover:bg-secondary/80 transition-colors"
                        data-testid="agent-email-link"
                      >
                        <Mail className="w-5 h-5 mr-3 text-accent" />
                        <span className="font-semibold text-primary">{property.agent_email}</span>
                      </a>
                    </div>

                    <Link to="/contact" state={{ propertyAddress: `${property.address.street_number} ${property.address.street_name} ${property.address.street_type}, ${property.address.city}` }}>
                      <Button 
                        className="w-full bg-primary text-primary-foreground hover:bg-primary/90 h-12 px-8 rounded-none uppercase tracking-widest text-sm font-semibold transition-all duration-300"
                        data-testid="request-info-button"
                      >
                        Request Information
                      </Button>
                    </Link>

                    <div className="mt-8 pt-8 border-t border-border">
                      <p className="text-sm text-muted-foreground text-center leading-relaxed">
                        Schedule a viewing or get more information about this property
                      </p>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default PropertyDetail;